import React, { useRef, useEffect, useState, useCallback } from "react";

function decodeAudio(arrayBuffer, numPeaks = 80) {
  return new Promise((resolve) => {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    ctx.decodeAudioData(arrayBuffer).then((audioBuffer) => {
      const data = audioBuffer.getChannelData(0);
      const step = Math.floor(data.length / numPeaks);
      const peaks = [];
      for (let i = 0; i < numPeaks; i++) {
        let sum = 0;
        const start = i * step;
        for (let j = 0; j < step; j++) {
          sum += Math.abs(data[start + j] || 0);
        }
        peaks.push(sum / step);
      }
      const max = Math.max(...peaks, 0.001);
      ctx.close();
      resolve({ audioBuffer, peaks: peaks.map((p) => Math.min(p / max, 1)) });
    }).catch(() => {
      ctx.close();
      resolve(null);
    });
  });
}

function drawWaveform(canvas, peaks, progress, isOwn) {
  if (!canvas || !peaks || peaks.length === 0) return;
  const ctx = canvas.getContext("2d");
  const w = canvas.width;
  const h = canvas.height;
  ctx.clearRect(0, 0, w, h);

  const barCount = peaks.length;
  const barWidth = Math.max((w / barCount) - 1, 2);
  const gap = 1;

  for (let i = 0; i < barCount; i++) {
    const val = peaks[i] || 0;
    const barH = Math.max(val * h * 0.85, 2);
    const x = i * (barWidth + gap);
    const y = (h - barH) / 2;
    const barProgress = i / barCount;

    if (barProgress <= progress) {
      ctx.fillStyle = isOwn ? "rgba(255,255,255,0.85)" : "#005c4b";
    } else {
      ctx.fillStyle = isOwn ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.2)";
    }

    ctx.beginPath();
    if (ctx.roundRect) {
      ctx.roundRect(x, y, barWidth, barH, 1);
    } else {
      ctx.rect(x, y, barWidth, barH);
    }
    ctx.fill();
  }
}

export default function AudioPlayer({ fileUrl, isOwn, duree, peaks: peaksProp }) {
  const canvasRef = useRef(null);
  const audioCtxRef = useRef(null);
  const sourceRef = useRef(null);
  const audioBufferRef = useRef(null);
  const startTimeRef = useRef(0);
  const offsetRef = useRef(0);

  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(duree || 0);
  const [speed, setSpeed] = useState(1);
  const [error, setError] = useState(false);
  const [ready, setReady] = useState(false);
  const animRef = useRef(null);
  const isPlayingRef = useRef(false);

  const peaksRef = useRef(peaksProp || []);

  useEffect(() => {
    isPlayingRef.current = isPlaying;
  }, [isPlaying]);

  useEffect(() => {
    if (peaksProp && peaksProp.length > 0) {
      peaksRef.current = peaksProp;
      setReady(true);
      drawWaveform(canvasRef.current, peaksProp, 0, isOwn);
    }
  }, [peaksProp, isOwn]);

  useEffect(() => {
    if (duree && duree > 0 && !audioBufferRef.current) {
      setDuration(duree);
    }
  }, [duree]);

  useEffect(() => {
    if (!fileUrl) return;
    setError(false);

    fetch(fileUrl, { credentials: "include" })
      .then((res) => {
        if (!res.ok) throw new Error("HTTP " + res.status);
        return res.arrayBuffer();
      })
      .then((buf) => decodeAudio(buf))
      .then((result) => {
        if (!result) { setError(true); return; }
        audioBufferRef.current = result.audioBuffer;
        peaksRef.current = result.peaks;
        setDuration(result.audioBuffer.duration);
        setReady(true);
        drawWaveform(canvasRef.current, result.peaks, 0, isOwn);
      })
      .catch((e) => {
        console.error("Audio load error:", e);
        setError(true);
      });

    return () => {
      if (animRef.current) cancelAnimationFrame(animRef.current);
      if (sourceRef.current) { try { sourceRef.current.stop(); } catch(e) {} }
      if (audioCtxRef.current) { audioCtxRef.current.close(); }
    };
  }, [fileUrl, isOwn]);

  const tick = useCallback(() => {
    if (!audioCtxRef.current || !duration) return;
    const elapsed = (audioCtxRef.current.currentTime - startTimeRef.current) * speed;
    const total = duration - offsetRef.current;
    const p = Math.min(elapsed / total, 1);
    const fullProgress = offsetRef.current / duration + p * (1 - offsetRef.current / duration);
    setProgress(fullProgress);
    drawWaveform(canvasRef.current, peaksRef.current, fullProgress, isOwn);
    if (elapsed < total) {
      animRef.current = requestAnimationFrame(tick);
    } else {
      setIsPlaying(false);
      setProgress(0);
      offsetRef.current = 0;
      drawWaveform(canvasRef.current, peaksRef.current, 0, isOwn);
    }
  }, [duration, speed, isOwn]);

  const ensureContext = () => {
    if (!audioCtxRef.current) {
      audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    if (audioCtxRef.current.state === "suspended") {
      audioCtxRef.current.resume();
    }
  };

  const startFrom = (offset) => {
    ensureContext();
    const ctx = audioCtxRef.current;
    const source = ctx.createBufferSource();
    source.buffer = audioBufferRef.current;
    source.playbackRate.value = speed;
    source.connect(ctx.destination);
    sourceRef.current = source;
    source.start(0, offset);
    startTimeRef.current = ctx.currentTime;
    offsetRef.current = offset;
    setIsPlaying(true);
    animRef.current = requestAnimationFrame(tick);

    source.onended = () => {
      if (isPlayingRef.current) {
        offsetRef.current = 0;
        setProgress(0);
        setIsPlaying(false);
        drawWaveform(canvasRef.current, peaksRef.current, 0, isOwn);
      }
    };
  };

  const stopSource = () => {
    if (sourceRef.current) { try { sourceRef.current.stop(); } catch(e) {} }
    if (animRef.current) cancelAnimationFrame(animRef.current);
  };

  const togglePlay = () => {
    if (!audioBufferRef.current) return;
    ensureContext();
    const ctx = audioCtxRef.current;

    if (isPlaying) {
      const elapsed = (ctx.currentTime - startTimeRef.current) * speed;
      offsetRef.current += elapsed;
      stopSource();
      setIsPlaying(false);
      setProgress(offsetRef.current / duration);
      drawWaveform(canvasRef.current, peaksRef.current, offsetRef.current / duration, isOwn);
      return;
    }

    startFrom(offsetRef.current);
  };

  const cycleSpeed = (e) => {
    e.stopPropagation();
    const speeds = [1, 1.5, 2];
    const next = speeds[(speeds.indexOf(speed) + 1) % speeds.length];
    setSpeed(next);
    if (isPlaying) {
      const ctx = audioCtxRef.current;
      const elapsed = (ctx.currentTime - startTimeRef.current) * speed;
      offsetRef.current += elapsed;
      stopSource();
      startFrom(offsetRef.current);
    }
  };

  const handleSeek = (e) => {
    if (!audioBufferRef.current || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const p = Math.max(0, Math.min(1, x / rect.width));
    const newOffset = p * duration;

    if (isPlaying) {
      stopSource();
      startFrom(newOffset);
    } else {
      offsetRef.current = newOffset;
      setProgress(p);
      drawWaveform(canvasRef.current, peaksRef.current, p, isOwn);
    }
  };

  const fmt = (sec) => {
    const s = Math.floor(sec || 0);
    const m = Math.floor(s / 60);
    return m + ":" + (s % 60).toString().padStart(2, "0");
  };

  if (error) {
    return (
      <div className="audio-player-wrapper" style={{ opacity: 0.5 }}>
        <span style={{ fontSize: 12 }}>Audio non disponible</span>
      </div>
    );
  }

  return (
    <div className="audio-player-wrapper">
      <button className="audio-player-play" onClick={togglePlay} disabled={!audioBufferRef.current}>
        {isPlaying ? (
          <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor">
            <rect x="6" y="5" width="4" height="14" rx="1"/>
            <rect x="14" y="5" width="4" height="14" rx="1"/>
          </svg>
        ) : (
          <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor">
            <path d="M8 5v14l11-7z"/>
          </svg>
        )}
      </button>

      <div className="audio-player-waveform" onClick={handleSeek} style={{ cursor: "pointer" }}>
        <canvas ref={canvasRef} width={400} height={32} style={{ width: "100%", height: 32 }} />
      </div>

      <button className="audio-player-speed" onClick={cycleSpeed} title={"Vitesse: " + speed + "x"} disabled={!audioBufferRef.current}>
        {speed}x
      </button>

      <span className="audio-player-time">
        {fmt(progress * duration || duration)}
      </span>
    </div>
  );
}
